import {
  Box,
  CircularProgress, Fade, IconButton,
  Stack, Switch, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, Typography,
} from "@mui/material";
import {FilterKB} from "../components/FilterKB";
import React, {useEffect, useState} from "react";
import {KnowledgeRepository} from "../../../../repositories/KnowledgeRepository";
import {KnowledgeDocumentTable} from "./KnowledgeDocumentTable";
import {KnowledgeVectorTable} from "./KnowledgeVectorTable";
import {IconChevronDown, IconChevronRight, IconTrash} from "@tabler/icons-react";
import {ResourceType} from "../ResourceManagement";
import {toast} from "react-toastify";
import {Chunks} from "../knowledge-details/components/Chunks";
import {MetadataTable} from "../knowledge-details/components/MetadataTable";
import GraphComponent from "../components/GraphComponent";
import {MetadataRepository} from "../../../../repositories/MetadataRepository";
import useConfirm from "../../../../hooks/useConfirm";

export const KnowledgeTable = ({project, organization, topics, slots}) => {
  const [knowledgeData, setKnowledgeData] = useState([]);
  const [filterParams, setFilterParams] = useState({
    organization: organization?.id,
    topic: null,
    slot: null,
    query: null,
    use_qa: false,
    report: false,
    knowledge_graphs: false
  })
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({page: 0, size: 10, totalPages: 1, totalElements: 1});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      fetchData();
    }
  }, [organization, filterParams?.topic, filterParams?.slot, filterParams?.query, filterParams?.use_qa, filterParams?.report, filterParams?.knowledge_graphs, pagination.page, pagination.size])

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await KnowledgeRepository.filterKnowledge({
        ...filterParams,
        organization_id: organization?.id,
        topic_id: filterParams?.topic || null,
        slot_id: filterParams?.slot || null,
        page: pagination?.page,
        size: pagination?.size,
      });
      // fetch(`https://api.kb.openbrain.io/api/neo4j/nodes`)
      KnowledgeRepository.getNeo4jNodes()
        .then(response => setData(response?.data || []))
        .catch(() => setData([]))
      setKnowledgeData(response.data.content || []);
      setPagination(prev => {
        if (prev) {
          return ({...prev, totalElements: response.data.totalElements, totalPages: response.data.totalPages})
        }
        return ({
          page: response.data.page,
          size: response.data.size,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages
        })
      })
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  const updateKnowledge = (data) => {
    fetchData()
  }

  const updateVectorKnowledge = (data) => {
    setKnowledgeData(prev => {
      return prev?.map(item => {
        if (item.chunk_id === data.chunk_id) {
          return data;
        }
        return item;
      })
    })
  }

  return (
    <Stack direction={"column"}
           sx={{mx: "auto", px: 1, flex: 1, height: '100%'}}>

      <FilterKB
        project={project}
        organization={organization}
        filterParams={filterParams} setFilterParams={setFilterParams}
        topics={topics} slots={slots}
        onCreate={fetchData}
      />

      {
        loading ?
          <Stack sx={{flex: 1}} alignItems={"center"} justifyContent={"center"}><CircularProgress/></Stack> :
          <Stack direction={"column"} sx={{flex: 1, overflowY: "auto"}}>
            <KbTable
              kbItems={knowledgeData} updateKnowledge={updateKnowledge} refetch={fetchData}
            />

          </Stack>
      }
      <TablePagination
        component="div"
        count={pagination?.totalElements}
        page={pagination?.page}
        // onPageChange={(e, page) => console.log(pagination.page, page)}
        onPageChange={(e, page) => setPagination(prev => ({...prev, page: page}))}
        rowsPerPage={pagination?.size}
        onRowsPerPageChange={(e) => setPagination(prev => ({...prev, page: 1, size: +e.target.value}))}
      />

    </Stack>
  )
}


export const KbTable = ({kbItems, refetch, updateKnowledge}) => {

  return (
    <Table stickyHeader className={"kb-table"}>
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell sx={{fontWeight: 600, fontSize: "0.8rem"}}>Content</TableCell>
          <TableCell sx={{fontWeight: 600, fontSize: "0.8rem"}}>Type</TableCell>
          <TableCell sx={{fontWeight: 600, fontSize: "0.8rem"}}><Typography fontSize={"inherit"} fontWeight={"inherit"}
                                                                            noWrap>Topic/Slot</Typography></TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {kbItems?.map(item => {
          return (
            <KbRow key={item?.data?.id} item={item} refetch={refetch} updateKnowledge={updateKnowledge}/>
          )
        })}
      </TableBody>
    </Table>
  )
}

export const KbRow = ({item, updateKnowledge, refetch}) => {
  const confirmDelete = useConfirm(
    'Are you sure you want to delete this item from graph?',
  );
  const [open, setOpen] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [metadata, setMetadata] = useState([]);

  const [loading, setLoading] = useState(false);
  const {data, query} = item;

  useEffect(() => {
    if (open) {
      fetchMetadata();
    }
  }, [data?.id, open])

  const fetchMetadata = () => {
    MetadataRepository.getByKnowledgeItemId(data.id)
      .then(response => {
        setMetadata(response?.data?.kg || []);
        setChunks(response?.data?.docs || []);
      })
      .catch(err => {
        console.log(err);
      })
  }

  const deleteItem = async (knowledge) => {
    const response = await confirmDelete();
    if (response) {
      KnowledgeRepository.delete(knowledge?.id)
        .then(() => {
          refetch();
          toast.success("Deleted successfully");
        })
        .catch((error) => {
          console.log(error);
          toast.error("Failed to delete");
        })
    }
  }

  const handlePublish = (knowledge) => {
    if (knowledge) {
      setLoading(true);
      KnowledgeRepository.publish(knowledge.id)
        .then((response) => {
          const data = response.data;
          updateKnowledge(data)
        })
        .catch(err => toast.error(err.message || "Failed to publish"))
        .finally(() => setLoading(false))
    }
  }

  return (
    <>
      <TableRow className={`kb-item ${open ? "selected" : ""}`}>
        <TableCell padding={"checkbox"} sx={{verticalAlign: "top", pt: 2}}>
          <IconButton size={"small"} onClick={() => setOpen(prev => !prev)}>{open ? <IconChevronDown size={18}/> :
            <IconChevronRight size={18}/>}</IconButton>
        </TableCell>
        <TableCell sx={{fontSize: "0.8rem", width: "100%"}}><Typography fontFamily={"Inter"}
          fontSize={"inherit"}>{data?.data?.substr(0, open ? data?.data?.length : 300) || "/"}{!open && data?.data?.length > 300 && "..."}</Typography></TableCell>
        <TableCell sx={{fontSize: "0.8rem", verticalAlign: "top"}}><Typography noWrap
                                                                               fontSize={"inherit"}>{ResourceType[data?.resourceType]}</Typography></TableCell>
        <TableCell padding={"checkbox"} sx={{fontSize: "0.8rem", verticalAlign: "top", py: 1}}>
          <Typography noWrap fontSize={"inherit"}>{data?.slot?.topic?.name}</Typography>
          <Typography noWrap fontSize={"inherit"}>{data?.slot?.name}</Typography>
        </TableCell>
        <TableCell padding={"checkbox"} sx={{verticalAlign: "top", pt: 2}}>
          <Stack direction={"row"} gap={1} alignItems={"center"} sx={{px: 2}}>
            <Switch size={"small"} disabled={loading} checked={!!data?.published}
                    onChange={() => handlePublish(data)}/>

            <IconButton color={"error"} onClick={() => deleteItem(data)}>
              <IconTrash size={18} />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      {
        open &&
        <Fade in={true}>
          <TableRow className={"selected"}>
            <TableCell colSpan={5}>
              <Stack direction={"row"} gap={3}>
                {/*<Stack direction={"column"} gap={1} sx={{flex: 4}}>*/}
                {/*  <Typography variant={"h4"} fontWeight={700}>{data?.id}. Resources</Typography>*/}
                {/*  /!*<Chunks knowledge={data} fetchData={refetch} query={null}/>*!/*/}
                {/*  {chunks?.map(item => <Typography key={item?.content}>{item?.content}</Typography>)}*/}
                {/*</Stack>*/}
               <MetadataTable metadata={metadata} />
              </Stack>
            </TableCell>
          </TableRow>
        </Fade>
      }
    </>
  )
}